import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity,
  CalendarDays,
  CreditCard,
  History,
  ShieldCheck,
  Stethoscope,
  WalletCards,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import ChartingTab from "@/components/charting/ChartingTab";
import OverviewChart from "@/components/charting/OverviewChart";
import MvVisitNotes from "@/components/charting/MvVisitNotes";
import TreatmentTab from "@/components/treatment/TreatmentTab";
import MedicalHistoryTab from "@/components/medical-history/MedicalHistoryTab";
import VisitsTab from "@/components/visits/VisitsTab";
import OrthoPanel from "@/components/ortho/OrthoPanel";
import PatientHmoTab from "@/components/patients/PatientHmoTab";
import PatientFilesTab from "@/components/patients/PatientFilesTab";
import MvCaseImportDialog from "@/components/treatment/MvCaseImportDialog";
import { isTenantFeatureEnabled } from "@/lib/tenantBillingAccess";
import { FeatureGate, useFeatures } from "@/context/FeaturesContext";
import { useAuth } from "@/context/AuthContext";
import { useOngoingTreatmentRollout } from "@/context/OngoingTreatmentRolloutContext";

// =====================================================================
// PatientTabs — patient detail workspace.
// Placeholder tabs stay only when the clinic still wants that surface visible.
// =====================================================================

const TABS = [
  { id: "overview",        tutorialId: "patient-tab-overview",        label: "Overview",        hint: "Snapshot of activity, top-line counts, and recent visits" },
  { id: "charting",        tutorialId: "patient-tab-charting",        label: "Charting",        hint: "Tooth chart with findings, treatment plans, and notes" },
  { id: "treatment",       tutorialId: "patient-tab-treatment",       label: "Treatment",       hint: "Open treatment needs, accepted plans, and ortho cases" },
  { id: "ortho",           tutorialId: "patient-tab-ortho",           label: "Ortho",           hint: "Orthodontic cases, billing setup, visits, and imported histories" },
  { id: "hmo",             tutorialId: "patient-tab-hmo",             label: "HMO",             hint: "Saved HMO cards used to prefill checkout claims" },
  { id: "visits",          tutorialId: "patient-tab-visits",          label: "Visits",          hint: "Completed visit history with payment details" },
  { id: "payments",        tutorialId: "patient-tab-payments",        label: "Payments",        hint: "Money events, allocations, and outstanding balance" },
  { id: "medical-history", tutorialId: "patient-tab-medical-history", label: "Medical History", hint: "Submitted medical history forms with revisions" },
  { id: "files",           tutorialId: "patient-tab-files",           label: "Files",           hint: "Uploaded x-rays, photos, consent forms, and other documents" },
  { id: "history",         tutorialId: "patient-tab-history",         label: "History",         hint: "Audit trail of every action recorded for this patient" },
];

export default function PatientTabs({ patientId, patient, tutorialDemoData = null }) {
  // ?tab=<id> deep-links from elsewhere in the app (e.g. the Tx Plan
  // Center drawer's "Mark Accepted" / "Open full chart" buttons land
  // here with ?tab=treatment&plan=<uuid>). On mount we read the param;
  // when the user clicks a tab we sync the URL too so refresh / share
  // stays consistent.
  const [searchParams, setSearchParams] = useSearchParams();
  const { clinicRole } = useAuth();
  const { access: tenantAccess } = useFeatures();
  const { enabled: ongoingTreatmentEnabled } = useOngoingTreatmentRollout();
  const [mvImportOpen, setMvImportOpen] = useState(false);
  const [mvImportNonce, setMvImportNonce] = useState(0);
  const [hasOpenMv, setHasOpenMv] = useState(false);
  const initialTab = (() => {
    const fromUrl = searchParams.get("tab");
    return TABS.some((t) => t.id === fromUrl) ? fromUrl : "overview";
  })();
  const [active, setActive] = useState(initialTab);
  const visibleTabs = TABS.filter((tab) => isPatientTabEnabled(tab.id, tenantAccess));

  useEffect(() => {
    const fromUrl = searchParams.get("tab");
    if (fromUrl && TABS.some((t) => t.id === fromUrl)) {
      if (fromUrl !== active) setActive(fromUrl);
      return;
    }
    if (!fromUrl) return;
    const sp = new URLSearchParams(searchParams);
    sp.delete("tab");
    setSearchParams(sp, { replace: true });
  }, [active, searchParams, setSearchParams]);

  function selectTab(next) {
    setActive(next);
    const sp = new URLSearchParams(searchParams);
    if (next === "overview") sp.delete("tab");
    else sp.set("tab", next);
    setSearchParams(sp, { replace: true });
  }

  // Cross-tab nav requests fire a `dos-jump-to-tab` window event so
  // child components can deep-link without prop-drilling. e.g. the
  // Medical History banner on the Treatment tab links here.
  useEffect(() => {
    function onJump(e) {
      if (typeof e?.detail === "string" && TABS.some((t) => t.id === e.detail)) {
        selectTab(e.detail);
      }
    }
    window.addEventListener("dos-jump-to-tab", onJump);
    return () => window.removeEventListener("dos-jump-to-tab", onJump);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (visibleTabs.some((tab) => tab.id === active)) return;
    selectTab("overview");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tenantAccess]);

  useEffect(() => {
    let cancelled = false;
    if (!ongoingTreatmentEnabled || tutorialDemoData || !patientId) {
      setHasOpenMv(false);
      setMvImportOpen(false);
      return undefined;
    }
    supabase.rpc("get_patient_bundle", { p_patient_id: patientId }).then(({ data }) => {
      if (!cancelled) setHasOpenMv(Boolean(data?.plan_id));
    });
    return () => { cancelled = true; };
  }, [ongoingTreatmentEnabled, patientId, tutorialDemoData, mvImportNonce]);

  const contentTab = visibleTabs.some((tab) => tab.id === active) ? active : "overview";
  const canImportMv = ongoingTreatmentEnabled
    && !hasOpenMv
    && !tutorialDemoData
    && ["clinic_admin", "doctor", "receptionist"].includes(clinicRole);
  return (
    <div className="space-y-4" data-testid="patient-tabs">
      {/* Tab bar */}
      <div className="bg-white rounded-2xl shadow-sm p-2" data-testid="patient-tab-bar">
        <div className="flex items-center gap-1 overflow-x-auto" role="tablist">
          {TABS.map((t) => {
            if (!visibleTabs.some((tab) => tab.id === t.id)) return null;
            const isActive = contentTab === t.id;
            const tabButton = (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => selectTab(t.id)}
                data-testid={`patient-tab-${t.id}`}
                data-tutorial-id={t.tutorialId}
                className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              >
                {t.label}
              </button>
            );
            const feature = featureForPatientTab(t.id);
            return feature ? <FeatureGate key={t.id} feature={feature}>{tabButton}</FeatureGate> : tabButton;
          })}
        </div>
      </div>

      {/* Tab content */}
      <div data-testid={`patient-tab-content-${contentTab}`}>
        {contentTab === "history"   ? <HistoryTab    patientId={patientId} demoRows={tutorialDemoData?.history} />
        : contentTab === "charting"  ? <ChartingTab   patientId={patientId} patient={patient} tutorialDemo={tutorialDemoData?.charting ?? null} />
        : contentTab === "treatment" ? (
          <div className="space-y-3">
            {canImportMv ? (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-violet-100 bg-violet-50/50 px-4 py-3" data-testid="patient-mv-import-entry">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Case already underway?</p>
                  <p className="text-xs text-slate-500">Record its package and opening balance, then continue the normal MV.</p>
                </div>
                <button type="button" onClick={() => setMvImportOpen(true)} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-2 text-xs font-semibold text-white hover:bg-violet-700" data-testid="patient-import-ongoing-case">
                  <History className="h-3.5 w-3.5" /> Import ongoing treatment
                </button>
              </div>
            ) : null}
            <TreatmentTab key={mvImportNonce} patientId={patientId} patient={patient} tutorialDemoData={tutorialDemoData?.treatment ?? null} />
          </div>
        )
        : contentTab === "ortho"     ? tutorialDemoData?.orthoPanel ? <OrthoPanel patientId={patientId} patient={patient} tutorialDemoData={tutorialDemoData.orthoPanel} /> : tutorialDemoData?.ortho ? <PatientOrthoSummaryTab ortho={tutorialDemoData.ortho} /> : <OrthoPanel patientId={patientId} patient={patient} />
        : contentTab === "hmo"       ? <PatientHmoTab patientId={patientId} />
        : contentTab === "medical-history" ? <MedicalHistoryTab patientId={patientId} patient={patient} demoHistory={tutorialDemoData?.medicalHistory ?? null} visitVitals={tutorialDemoData?.medicalHistory?.visitVitals ?? null} />
        : contentTab === "visits"    ? <VisitsTab     patientId={patientId} demoVisits={tutorialDemoData?.visits ?? null} demoVisitDetailsById={tutorialDemoData?.visitDetailsById ?? null} />
        : contentTab === "payments"  ? tutorialDemoData?.payments ? <PatientPaymentsTab payments={tutorialDemoData.payments} planBalance={tutorialDemoData.treatment?.planBalance} /> : <TabPlaceholder tab={TABS.find((t) => t.id === contentTab)} />
        : contentTab === "files"     ? <PatientFilesTab patientId={patientId} patient={patient} demoFiles={tutorialDemoData?.files ?? null} />
        : contentTab === "overview"  ? <OverviewTab   patientId={patientId} patient={patient} onJumpToTab={selectTab} tutorialDemoData={tutorialDemoData?.overviewChart ?? null} ongoingTreatmentEnabled={ongoingTreatmentEnabled} />
        : <TabPlaceholder tab={TABS.find((t) => t.id === contentTab)} />}
      </div>
      {canImportMv ? (
        <MvCaseImportDialog
          open={mvImportOpen}
          patientId={patientId}
          patientName={patient?.full_name}
          sourceEntryPoint="patient_record"
          onClose={() => setMvImportOpen(false)}
          onImported={() => setMvImportNonce((value) => value + 1)}
        />
      ) : null}
    </div>
  );
}

function isPatientTabEnabled(tabId, tenantAccess) {
  const feature = featureForPatientTab(tabId);
  return feature ? isTenantFeatureEnabled(tenantAccess, feature) : true;
}

function featureForPatientTab(tabId) {
  if (tabId === "treatment") return "tx_plan_center";
  if (tabId === "charting") return "tx_plan_charting";
  if (tabId === "ortho") return "ortho_module";
  if (tabId === "medical-history") return "medical_history";
  return null;
}
function TabPlaceholder({ tab }) {
  return (
    <div data-testid="patient-tab-placeholder" className="bg-white rounded-2xl shadow-sm p-12 text-center">
      <h2 className="text-xl font-bold text-slate-900">{tab.label}</h2>
      <p className="mt-3 text-sm text-slate-500">Coming soon — this tab will be wired in a later build round.</p>
      <p className="mt-1 text-xs text-slate-400 max-w-md mx-auto">{tab.hint}.</p>
    </div>
  );
}

// ---- Overview tab ----------------------------------------------------
// First impression of the patient. Right now it surfaces just the
// Dynamic Charting glance card; future rounds will stack additional
// sections here (next appointment, recent activity, balance summary).
function OverviewTab({ patientId, patient, onJumpToTab, tutorialDemoData, ongoingTreatmentEnabled }) {
  return (
    <div className="space-y-4" data-testid="overview-tab">
      <OverviewChart patientId={patientId} patient={patient} onJumpToTab={onJumpToTab} tutorialDemoData={tutorialDemoData} />
      {ongoingTreatmentEnabled ? <PatientMvNotes patientId={patientId} /> : null}
    </div>
  );
}

// ---- Patient MV visit-note history (read-only) ----------------------
// Surfaces the locked per-visit notes from every on-the-go (MV) treatment
// this patient has had — the diary survives here after the live Ongoing
// Treatment card closes. Read-only: no editor, no addendum controls
// (appointmentId=null + no onAddendum). Renders nothing when there are no
// notes, so the Overview stays clean for patients who never had an MV.
function PatientMvNotes({ patientId }) {
  const [mvNotes, setMvNotes] = useState([]);

  const loadMvNotes = useCallback(async () => {
    if (!patientId) { setMvNotes([]); return; }
    const { data, error } = await supabase.rpc("get_patient_mv_notes", { p_patient_id: patientId });
    if (error) { toast.error(error.message ?? "Could not load visit notes."); return; }
    setMvNotes(Array.isArray(data) ? data : []);
  }, [patientId]);

  useEffect(() => { loadMvNotes(); }, [loadMvNotes]);

  if (mvNotes.length === 0) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="patient-mv-notes">
      <MvVisitNotes
        notes={{ next_visit_plan: null, last_note: null, current_note: null, history: mvNotes }}
        appointmentId={null}
        onSave={undefined}
        onAddendum={undefined}
      />
    </div>
  );
}

// ---- History tab (wired) -------------------------------------------
function HistoryTab({ patientId, demoRows = null }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(() => !demoRows);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (demoRows) {
      setRows(demoRows);
      setLoading(false);
      setError(null);
      return undefined;
    }
    setLoading(true);
    setError(null);
    supabase.rpc("list_patient_history", { p_id: patientId }).then(({ data, error: err }) => {
      setLoading(false);
      if (err) { setError(err.message ?? "Could not load history."); return; }
      setRows(data ?? []);
    });
  }, [patientId, demoRows]);

  if (loading) return <div data-testid="patient-history-loading" className="text-sm text-slate-400 px-2">Loading history…</div>;
  if (error)   return <div data-testid="patient-history-error"   className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-700">{error}</div>;
  if (rows.length === 0) {
    return (
      <div data-testid="patient-history-empty" className="bg-white rounded-2xl shadow-sm p-12 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 mx-auto flex items-center justify-center"><Activity className="w-5 h-5 text-slate-400" /></div>
        <h3 className="mt-4 text-base font-semibold text-slate-900">No history events recorded.</h3>
        <p className="text-sm text-slate-500 mt-1">Activity will appear here as you and your team work with this patient.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="patient-history-list">
      <ol className="relative border-l border-slate-200 ml-3 space-y-5">
        {rows.map((r) => (
          <li key={r.id} className="ml-5 relative" data-testid={`patient-history-entry-${r.id}`}>
            <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-teal-500 border-2 border-white shadow"></span>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm text-slate-900 font-medium leading-snug">{r.description}</p>
                <p className="text-[11px] text-slate-500 mt-1">
                  <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{r.action_name}</code>
                  {r.user_name ? <span className="ml-2">by {r.user_name}</span> : r.user_email ? <span className="ml-2">by {r.user_email}</span> : null}
                </p>
              </div>
              <time className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">
                {new Date(r.occurred_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
              </time>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function PatientPaymentsTab({ payments, planBalance }) {
  const totalPaid = payments.reduce((sum, payment) => sum + Number(payment.amount_centavos || 0), 0);
  return (
    <div className="space-y-4" data-testid="patient-payments-tab">
      <section className="bg-white rounded-2xl shadow-sm p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <PaymentStat icon={WalletCards} label="Collected" value={formatPHP(totalPaid)} tone="strong" />
          <PaymentStat icon={CreditCard} label="Tx Plan paid" value={formatPHP(planBalance?.paid_centavos)} />
          <PaymentStat icon={CalendarDays} label="Tx Plan remaining" value={formatPHP(planBalance?.remaining_centavos)} tone="warn" />
        </div>
      </section>

      <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-900">Payment history</h2>
          <p className="text-xs text-slate-500 mt-0.5">Confirmed payments connected to completed work and the active Tx Plan.</p>
        </div>
        <ul className="divide-y divide-slate-100">
          {payments.map((payment) => (
            <li key={payment.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`patient-payment-${payment.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900">{payment.method}</span>
                  <span className="text-[11px] font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">{payment.status}</span>
                  <span className="text-xs text-slate-400">{formatDate(payment.date)}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{payment.appliesTo}</p>
                {payment.reference ? <p className="text-[11px] text-slate-400 mt-1">Ref {payment.reference}</p> : null}
              </div>
              <div className="text-base font-bold text-slate-900 tabular-nums">{formatPHP(payment.amount_centavos)}</div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function PaymentStat({ icon: Icon, label, value, tone = "normal" }) {
  const toneClass = tone === "warn" ? "text-amber-700" : tone === "strong" ? "text-emerald-700" : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`mt-1.5 text-lg font-bold ${toneClass}`}>{value}</div>
    </div>
  );
}

function PatientOrthoSummaryTab({ ortho }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="patient-ortho-summary-tab">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-teal-700" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-slate-500">Ortho screening</p>
          <h2 className="mt-1 text-base font-semibold text-slate-900">{ortho.status}</h2>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">{ortho.note}</p>
          <p className="mt-3 text-xs text-slate-500 inline-flex items-center gap-1.5">
            <Stethoscope className="w-3.5 h-3.5 text-slate-400" />
            {ortho.checkedBy} · {formatDate(ortho.checkedAt)}
          </p>
        </div>
      </div>
    </div>
  );
}

function formatPHP(centavos) {
  const n = (centavos ?? 0) / 100;
  return `₱${n.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(iso) {
  if (!iso) return "No date";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
